<template>
  <div class="encounter-interface">
    <header
      class="encounter-interface__header"
      v-sticky
      sticky-z-index="8"
      ref="header"
    >
      <stats-header
        :blood-pressure="bloodPressure"
        :weight="weight"
        :cholesterolLDL="cholesterolLDL"
        :glucose="glucose"
        :knowledge-level-name="knowledgeLevelName"
        :behaviour-goals="behaviourGoals"
        :outcome-goals="outcomeGoals"
        :bmi="BMI"
        :bmi-date="BMIDate"
      >
        <template v-slot:button>
          <v-btn
            outlined
            icon
            color="primary"
            @click="$router.push({ name: 'PatientOverview' })"
          >
            <v-icon :color="$color.coro.purple">mdi-close</v-icon>
          </v-btn>
        </template>
      </stats-header>
    </header>
    <div class="create-encounter__content standard-layout">
      <h2 class="mb-8 standard-layout__heading">{{ $ucfirst($t('encounter.start')) }}</h2>
      <encounter-button-line
        :isMedicationDSSAvailable="isMedicationDSSAvailable"
        :is6MWTAvailable="is6MWTAvailable"
      ></encounter-button-line>
      <router-view></router-view>
      <div class="d-flex mb-8 mt-4">
        <v-btn
          v-if="isPrevious"
          x-large
          text
          @click="goToPrevious"
          class="mr-3"
        >
          <eva-icon
            class="encounter-interface__icon"
            name="arrow-left-outline"
          />
          {{ $ucfirst($t('previous')) }}
        </v-btn>
        <v-btn
          v-if="isNext"
          x-large
          color="primary"
          text
          @click="goToNext"
          class="mr-3"
        >
          {{ $ucfirst($t('buttons.next', {catalog: 'app_mobile'})) }}
          <eva-icon
            class="encounter-interface__icon"
            name="arrow-right-outline"
          />
        </v-btn>
        <v-btn
            v-else-if="canEditVisitData"
            x-large
            color="primary"
            text
            @click="submit"
            class="mr-3"
        >
          {{ $ucfirst($t(isExisting ? 'dss.savePrescription' : 'startConsultation')) }}
        </v-btn>
        <v-btn
          v-else
          x-large
          color="primary"
          text
          @click="() => $router.push({ name: 'PatientOverview' })"
          class="mr-3"
        >
          {{ $ucfirst($t('dss.goToPatientOverview')) }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import StatsHeader from '../../components/Parts/StatsHeader';
import EncounterButtonLine from './EncounterButtonLine';
import validation from 'dashboard/store/encounter/validation';

/**
 * Component EncounterInterface.vue
 *
 * @module EncounterInterface
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/views/Encounter/EncounterInterface.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'EncounterInterface',
  components: {
    StatsHeader,
    EncounterButtonLine,
  },
  props: {
    version: {
      type: [String, Number],
      required: true,
    }
  },
  data() {
    return {
      isExisting: false
    }
  },
  mounted() {
    this.findExistingEncounter();
  },
  computed: {
    ...mapGetters({
      bloodPressure: 'patientSummary/bloodPressure',
      weight: 'patientSummary/weight',
      cholesterolLDL: 'patientSummary/cholesterolLDL',
      glucose: 'patientSummary/glucose',
      knowledgeLevelName: 'patientSummary/knowledgeLevelName',
      behaviourGoals: 'patientSummary/behaviourGoals',
      outcomeGoals: 'patientSummary/outcomeGoals',
      BMI: 'patientSummary/BMI',
      BMIDate: 'patientSummary/BMIDate',
      studyId: 'patientSummary/studyId',
      patientId: 'patientSummary/id'
    }),
    canEditVisitData(){
      return this.$store.getters['user/canEditVisitData'](this.$store.getters['encounter/versionInt'])
    },
    isMedicationDSSAvailable() {
      return [2, 6].includes(parseInt(this.version));
    },
    is6MWTAvailable() {
      return [2, 6, 7].includes(parseInt(this.version));
    },
    isPrevious() {
      return this.$route.name !== this.steps[0];
    },
    isNext() {
      return this.$route.name !== this.steps[this.steps.length - 1];
    },
    steps() {
      const steps = ['PatientEncounterVitalSigns'];

      if (this.is6MWTAvailable) {
        steps.push('PatientEncounterWalkingTest');
      }

      steps.push('PatientEncounterClinicalAssessment');

      if (this.isMedicationDSSAvailable) {
        steps.push('PatientEncounterMedicationDSS');
      }

      return steps;
    },
    currentIndex() {
      return this.steps.indexOf(this.$route.name);
    }
  },
  methods: {
    goToPrevious() {
      if (this.currentIndex < 0) return;
      this.$router.push({ name: this.steps[this.currentIndex - 1] });
    },
    goToNext() {
      if (this.currentIndex < 0) return;
      this.$router.push({ name: this.steps[this.currentIndex + 1] });
    },
    async submit() {
      const isValid = this.$store.getters['encounter/isValid'];
      const invalidFields = this.$store.getters['encounter/invalidFields'];

      if (isValid) {
        this.$store.dispatch('encounter/switchToNormalMode');
        const success = await this.$store.dispatch('encounter/submit');
        const existingEncounter = await this.$store.dispatch('encounter/findExistingEncounter');

        if (success && !existingEncounter) {
          await this.$store.dispatch('followupSession/startSDM');
          this.$router.push({ name: 'PatientRecordProgressStatusJourney' });
          this.$toasted.show(
            this.$ucfirst(
              this.$t("dummy.SDM.new_SDM_started", {
                params: { patientName: this.studyId },
              })
            ), { type: "success" }
          );
        } else if (success && existingEncounter) {

          await this.$store.dispatch('patientSummary/update');
          // await this.$store.dispatch('alerts/getRecent', this.patientId, { root: true });

          this.$router.push({ name: 'PatientOverview' });
          this.$toasted.show(this.$ucfirst(this.$t('successMessages.encounterUpdated')), {
            type : 'success',
            icon : 'check',
            position: 'bottom-center',
          });
        }
      } else {
        this.$store.dispatch('encounter/switchToNormalMode');
        this.$store.dispatch('encounter/switchToValidationMode');
        const { page } = validation[invalidFields[0]];

        if (this.$route.name !== page) {
          this.$router.push({ name: page });
        }
      }
    },
    async findExistingEncounter() {
      const exists = await this.$store.dispatch('encounter/findExistingEncounter')
      this.isExisting = !!exists
    },
  },
};
</script>

<style lang="scss">
.encounter-interface__header {
  background-color: #fff;
}

.encounter-interface__icon {
  height: 24px;
}

.encounter-form__table {
  table > tbody > tr:hover {
    background-color: transparent !important;
  }

  td {
    font-size: 0.95rem !important;
    font-weight: 600;
    white-space: nowrap;
    padding-right: 32px;
  }

  td:not(:first-child) {
    width: 99%;
  }

  td.title {
    font-size: 1.1rem !important;
    font-weight: 700;
  }

  .v-text-field {
    margin-top: 28px;
    width: 225px;
  }

  .v-input--radio-group {
    margin-top: 24px;

    .v-messages {
      margin-bottom: 8px;
    }
  }
}

.encounter-form__row {
  display: flex;
  align-content: center;
}

.encounter-form__label {
  width: 180px;
  margin-right: 32px;
  font-weight: 600;
  padding-bottom: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.encounter-form__input {
  width: 220px;
  margin-right: 32px;
}

.encounter-form__radio-group {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>
